import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

/**
 * Fängt unbehandelte Render-/Lifecycle-/Effekt-Fehler im gesamten App-Baum ab.
 * Ohne diese Boundary hängt React bei einem einzigen Throw den kompletten Baum aus
 * → weißer Bildschirm ohne jeden Hinweis. Stattdessen zeigen wir eine gebrandete
 * Fehlerseite mit Reload, sodass ein transienter Fehler nie als Totalausfall wirkt.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // In die Konsole, damit der Fehler im Browser-Devtools/Monitoring sichtbar bleibt.
    console.error('Unbehandelter Fehler in der App:', error, info.componentStack);
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;

    return (
      <div className="flex h-screen w-screen flex-col items-center justify-center bg-kalkuel-primary px-6 text-center text-white">
        <div className="flex h-14 w-14 items-center justify-center rounded-md bg-white/10 ring-1 ring-white/20">
          <svg
            viewBox="0 0 24 24"
            className="h-8 w-8 text-kalkuel-accent"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <path d="M4 4v16" strokeLinecap="round" />
            <path d="M20 4L8 12l12 8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div className="mt-6 text-xs font-light uppercase tracking-wider text-kalkuel-accent">
          kalkül Inbox
        </div>
        <h1 className="mt-2 text-lg font-semibold">Etwas ist schiefgelaufen</h1>
        <p className="mt-2 max-w-sm text-sm text-white/70">
          Die Ansicht konnte nicht geladen werden. Bitte lade die Seite neu. Tritt das Problem
          erneut auf, melde dich beim IT-Service.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="mt-6 rounded-pill bg-kalkuel-accent px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-kalkuel-accent2"
        >
          Seite neu laden
        </button>
      </div>
    );
  }
}
